/*
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { existsSync, readFileSync } from 'fs';
import { join } from 'path';
import type { DetectorResult } from '../types/index.js';
import { createDetectorResult, type Detector } from './base.js';

/**
 * Docker Detector
 *
 * Detects Dockerized projects and determines the best command to run them.
 * Supports both Dockerfile and docker-compose.yml configurations.
 */
export class DockerDetector implements Detector {
  name = 'docker';

  async detect(cwd: string): Promise<DetectorResult> {
    const hasDockerfile = existsSync(join(cwd, 'Dockerfile'));
    const hasDockerCompose =
      existsSync(join(cwd, 'docker-compose.yml')) ||
      existsSync(join(cwd, 'docker-compose.yaml')) ||
      existsSync(join(cwd, 'compose.yml')) ||
      existsSync(join(cwd, 'compose.yaml'));

    // Need at least one Docker file to detect
    if (!hasDockerfile && !hasDockerCompose) {
      return createDetectorResult(this.name, 0);
    }

    // Wait for detection to complete (satisfies async requirement)
    await Promise.resolve();

    const reasons: string[] = [];
    let confidence = 0;

    // Prefer docker-compose if available (more complete setup)
    if (hasDockerCompose) {
      const composeFile = findComposeFile(cwd);
      const services = parseComposeServices(cwd, composeFile);

      reasons.push(`Found ${composeFile}`);
      confidence += 40;

      if (services.length > 0) {
        reasons.push(`Found ${services.length} service(s): ${services.join(', ')}`);
        confidence += 20;
      }

      // Check if there's a dev service
      const hasDevService = services.some((s) =>
        s.toLowerCase().includes('dev')
      );
      if (hasDevService) {
        reasons.push('Found development service');
        confidence += 10;
      }

      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: 'docker',
        args: ['compose', 'up'],
        reasons,
        detectedType: 'Docker Compose',
      });
    }

    // Fallback to Dockerfile
    if (hasDockerfile) {
      reasons.push('Found Dockerfile');
      confidence += 30;

      // Check if there's a .dockerignore (better setup)
      if (existsSync(join(cwd, '.dockerignore'))) {
        reasons.push('Found .dockerignore file');
        confidence += 5;
      }

      // Try to parse image name from Dockerfile
      const imageName = parseDockerfileImageName(cwd);
      if (imageName) {
        reasons.push(`Image: ${imageName}`);
        confidence += 10;
      }

      return createDetectorResult(this.name, confidence, {
        cwd,
        pm: null,
        cmd: 'docker',
        args: ['build', '-t', imageName || 'app', '.'],
        reasons: [
          ...reasons,
          'Note: Use "docker run <image>" to start the container',
        ],
        detectedType: 'Docker',
      });
    }

    return createDetectorResult(this.name, 0);
  }
}

/**
 * Find the docker-compose file (supports multiple names)
 */
function findComposeFile(cwd: string): string {
  const possibleFiles = [
    'docker-compose.yml',
    'docker-compose.yaml',
    'compose.yml',
    'compose.yaml',
  ];

  for (const file of possibleFiles) {
    if (existsSync(join(cwd, file))) {
      return file;
    }
  }

  return 'docker-compose.yml'; // default fallback
}

/**
 * Parse docker-compose.yml to extract service names
 */
function parseComposeServices(
  cwd: string,
  composeFile: string
): string[] {
  try {
    const composePath = join(cwd, composeFile);
    if (!existsSync(composePath)) return [];

    const content = readFileSync(composePath, 'utf-8');

    // Simple YAML parsing for services (not using a full YAML parser for simplicity)
    const serviceMatch = content.match(/services:\s*\n([\s\S]*?)(\n\w+:|$)/);
    if (!serviceMatch) return [];

    const servicesBlock = serviceMatch[1];
    const serviceNames = servicesBlock
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#') && line.endsWith(':'))
      .map((line) => line.replace(':', '').trim())
      .filter((name) => name && name !== 'services');

    return serviceNames;
  } catch {
    return [];
  }
}

/**
 * Parse Dockerfile to try to extract image name or app name
 */
function parseDockerfileImageName(cwd: string): string | null {
  try {
    const dockerfilePath = join(cwd, 'Dockerfile');
    if (!existsSync(dockerfilePath)) return null;

    const content = readFileSync(dockerfilePath, 'utf-8');

    // Try to find LABEL or comment with app name
    const labelMatch = content.match(/LABEL\s+(?:name|app)=["']?([^"'\s]+)/i);
    if (labelMatch) return labelMatch[1];

    // Fallback: use directory name
    const dirName = cwd.split('/').pop() || 'app';
    return dirName.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  } catch {
    return null;
  }
}

